(() => {
  if (customElements.get('sg-collection-catalogue')) return;
  const indexCache = new Map();
  class CollectionCatalogue extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.onClick = this.click.bind(this);
      this.onInput = this.input.bind(this);
      this.onChange = this.change.bind(this);
      this.onSubmit = this.submit.bind(this);
      this.onKey = this.key.bind(this);
      this.onPop = () => this.navigate(location.href, false, false);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKey);
      window.addEventListener('popstate', this.onPop);
      this.querySelectorAll('[data-sgc-min],[data-sgc-max]').forEach(input => { input.disabled = false; });
      this.q = new URL(location.href).searchParams.get('sgc_q') || '';
      if (this.categoryMarkup) {
        const template = document.createElement('template'); template.innerHTML = this.categoryMarkup;
        const categories = template.content.firstElementChild;
        const current = this.querySelector('.sgc-categories');
        if (current) current.replaceWith(categories); else this.querySelector('.sgc-catalogue').before(categories);
      }
      this.categoryMarkup = this.querySelector('.sgc-categories')?.outerHTML;
      const params = new URL(location.href).searchParams;
      this.querySelectorAll('[data-sgc-category-param]').forEach(link => {
        const active = params.getAll(link.dataset.sgcCategoryParam).includes(link.dataset.sgcCategoryValue) || Boolean(link.dataset.sgcCategoryExtra && params.getAll(link.dataset.sgcCategoryParam).includes(link.dataset.sgcCategoryExtra));
        link.classList.toggle('is-active', active); link.dataset.sgcCategoryActive = String(active);
      });
      this.querySelector('[data-sgc-category-reset]')?.classList.toggle('is-active', !params.has('filter.p.m.custom.sport') && !params.has('filter.p.m.custom.design_style'));
      this.querySelector('[data-sgc-search]').value = this.q;
      this.querySelector('[data-sgc-clear-search]').hidden = !this.q;
      this.panel = this.querySelector('.sgc-filters');
      this.panel.setAttribute('aria-hidden', String(matchMedia('(max-width:980px)').matches));
      this.mobileQuery = matchMedia('(max-width:980px)');
      this.onMedia = () => this.closeDrawer(false);
      this.mobileQuery.addEventListener('change', this.onMedia);
      if (this.q) this.search();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.searchController?.abort();
      window.removeEventListener('popstate', this.onPop);
      this.mobileQuery?.removeEventListener('change', this.onMedia);
      document.body.classList.remove('sgc-drawer-open');
    }
    url(value) {
      const url = new URL(value, location.href);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }
    setBusy(value) { this.querySelector('.sgc-catalogue').setAttribute('aria-busy', String(value)); }
    async section(url, signal) {
      const request = this.url(url);
      request.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(request, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Collection request ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = doc.querySelector('sg-collection-catalogue');
      if (!root) throw new Error('Collection section missing');
      return root;
    }
    async navigate(value, push = true, keepSearch = true) {
      const url = this.url(value);
      if (keepSearch && this.q) url.searchParams.set('sgc_q', this.q);
      this.controller?.abort();
      this.controller = new AbortController();
      this.closeDrawer(false);
      this.setBusy(true);
      try {
        const replacement = await this.section(url, this.controller.signal);
        if (push) history.pushState({}, '', url);
        if ([...url.searchParams.keys()].some(key => key.startsWith('filter.'))) replacement.categoryMarkup = this.categoryMarkup;
        this.replaceWith(replacement);
      } catch (error) {
        if (error.name !== 'AbortError') location.assign(url);
      }
    }
    openDrawer() {
      this.restoreFocus = document.activeElement;
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.querySelector('.sgc-backdrop').classList.add('is-open');
      this.querySelector('[data-sgc-open]').setAttribute('aria-expanded', 'true');
      document.body.classList.add('sgc-drawer-open');
      this.panel.querySelector('[data-sgc-close]').focus();
    }
    closeDrawer(focus = true) {
      if (!this.panel) return;
      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', String(matchMedia('(max-width:980px)').matches));
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.querySelector('.sgc-backdrop').classList.remove('is-open');
      this.querySelector('[data-sgc-open]').setAttribute('aria-expanded', 'false');
      document.body.classList.remove('sgc-drawer-open');
      if (focus) this.restoreFocus?.focus();
    }
    click(event) {
      const target = event.target;
      if (target.closest('[data-sgc-open]')) { this.openDrawer(); return; }
      if (target.closest('[data-sgc-close]')) { this.closeDrawer(); return; }
      const size = target.closest('[data-sgc-size]');
      if (size) {
        const card = size.closest('[data-sgc-card]');
        card.querySelectorAll('[data-sgc-size]').forEach(button => button.setAttribute('aria-pressed', String(button === size)));
        card.querySelector('[data-sgc-price]').textContent = size.dataset.price;
        card.querySelectorAll('[data-sgc-product-link],.sg-card-gallery__link').forEach(link => { link.href = size.dataset.url; });
        return;
      }
      if (target.closest('[data-sgc-clear-search]')) {
        event.preventDefault();
        clearTimeout(this.timer);
        this.q = '';
        const url = new URL(location.href); url.searchParams.delete('sgc_q'); url.searchParams.delete('page');
        this.navigate(url, true, false); return;
      }
      const link = target.closest('[data-sgc-filter-link],[data-sgc-reset],[data-sgc-category-reset],[data-sgc-more],[data-sgc-page],[data-sgc-search-more]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      if (link.hasAttribute('data-sgc-search-more')) { this.searchLimit += 24; this.drawSearch(); return; }
      if (link.hasAttribute('data-sgc-more')) { this.more(link.href); return; }
      if (link.hasAttribute('data-sgc-reset')) { this.q = ''; this.navigate(link.href, true, false); return; }
      if (link.hasAttribute('data-sgc-category-reset')) {
        const url = new URL(location.href);
        url.searchParams.delete('filter.p.m.custom.sport');
        url.searchParams.delete('filter.p.m.custom.design_style');
        url.searchParams.delete('page');
        this.navigate(url); return;
      }
      if (link.dataset.sgcCategoryParam) {
        const url = new URL(location.href);
        url.searchParams.delete('filter.p.m.custom.sport');
        url.searchParams.delete('filter.p.m.custom.design_style');
        url.searchParams.delete('page');
        if (link.dataset.sgcCategoryActive !== 'true') {
          url.searchParams.set(link.dataset.sgcCategoryParam, link.dataset.sgcCategoryValue);
          if (link.dataset.sgcCategoryExtra) url.searchParams.append(link.dataset.sgcCategoryParam, link.dataset.sgcCategoryExtra);
        }
        this.navigate(url); return;
      }
      this.navigate(link.href);
    }
    input(event) {
      if (event.target.matches('[data-sgc-team-search]')) {
        const text = event.target.value.toLocaleLowerCase().trim();
        this.querySelectorAll('[data-sgc-team]').forEach(label => { label.hidden = !label.dataset.sgcTeam.includes(text); });
      }
      if (event.target.matches('[data-sgc-search]')) {
        this.q = event.target.value.trim();
        this.querySelector('[data-sgc-clear-search]').hidden = !this.q;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 220);
      }
    }
    change(event) {
      const input = event.target;
      if (input.closest('[data-sgc-sort-form]')) { input.form.requestSubmit(); return; }
      if (!input.closest('[data-sgc-filter-form]')) return;
      if (input.matches('[data-sgc-price-band]')) {
        const ranges = { under100: ['', '99.99'], '100to150': ['100', '149.99'], '150plus': ['150', ''] };
        const range = ranges[input.value] || ['', ''];
        input.form.querySelector('[data-sgc-min]').value = range[0];
        input.form.querySelector('[data-sgc-max]').value = range[1];
      }
      if (matchMedia('(min-width:981px)').matches) input.form.requestSubmit();
    }
    submit(event) {
      if (event.target.matches('.sgc-search')) {
        event.preventDefault(); clearTimeout(this.timer); this.search(); return;
      }
      const form = event.target.closest('[data-sgc-filter-form],[data-sgc-sort-form]');
      if (!form) return;
      event.preventDefault();
      const url = this.url(form.action);
      new FormData(form).forEach((value, key) => { if (String(value).trim() !== '') url.searchParams.append(key, value); });
      this.navigate(url);
    }
    key(event) {
      if (!this.panel.classList.contains('is-open')) return;
      if (event.key === 'Escape') { event.preventDefault(); this.closeDrawer(); }
      if (event.key !== 'Tab') return;
      const items = [...this.panel.querySelectorAll('a,button,input,select')].filter(el => !el.disabled && el.offsetParent !== null && el.type !== 'hidden');
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    async more(value) {
      this.controller?.abort(); this.controller = new AbortController(); this.setBusy(true);
      try {
        const root = await this.section(value, this.controller.signal);
        this.querySelector('[data-sgc-grid]').append(...root.querySelectorAll('[data-sgc-card]'));
        this.querySelector('[data-sgc-pagination]').replaceWith(root.querySelector('[data-sgc-pagination]'));
        const shown = this.querySelectorAll('[data-sgc-grid] [data-sgc-card]').length;
        this.querySelector('[data-sgc-shown]').textContent = `Showing ${shown} of ${this.dataset.total} products`;
      } catch (error) { if (error.name !== 'AbortError') location.assign(value); }
      finally { if (this.isConnected) this.setBusy(false); }
    }
    async getIndex() {
      const url = this.url(location.href);
      ['sgc_q','page','section_id','sections'].forEach(key => url.searchParams.delete(key));
      url.searchParams.set('view', 'sgc-search');
      const key = url.toString();
      if (indexCache.has(key)) return indexCache.get(key);
      this.searchController = new AbortController();
      const signal = this.searchController.signal;
      const fetchPage = async page => {
        const next = new URL(url); next.searchParams.set('page', page);
        const response = await fetch(next, { signal });
        if (!response.ok) throw new Error('Collection search unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const index = doc.querySelector('[data-sgc-index]');
        if (!index) throw new Error('Collection search index missing');
        return index;
      };
      const promise = (async () => {
        const first = await fetchPage(1);
        const cards = [...first.querySelectorAll('[data-sgc-card]')];
        const pages = Number(first.dataset.pages) || 1;
        for (let page = 2; page <= pages; page += 2) {
          const batch = [fetchPage(page)];
          if (page + 1 <= pages) batch.push(fetchPage(page + 1));
          for (const index of await Promise.all(batch)) cards.push(...index.querySelectorAll('[data-sgc-card]'));
        }
        return cards;
      })();
      indexCache.set(key, promise);
      try { return await promise; }
      catch (error) { indexCache.delete(key); throw error; }
    }
    async search() {
      const q = this.q;
      const url = new URL(location.href); url.searchParams.delete('page');
      if (!q) { url.searchParams.delete('sgc_q'); this.navigate(url, true, false); return; }
      url.searchParams.set('sgc_q', q); history.replaceState({}, '', url);
      this.setBusy(true);
      this.querySelector('[data-sgc-count]').textContent = 'Searching…';
      try {
        const cards = await this.getIndex();
        if (!this.isConnected || this.q !== q) return;
        const words = q.toLocaleLowerCase().split(/\s+/).filter(Boolean);
        this.matches = cards.filter(card => words.every(word => card.dataset.search.includes(word)));
        this.searchLimit = 24;
        this.drawSearch();
      } catch (error) {
        if (error.name === 'AbortError' || !this.isConnected || this.q !== q) return;
        const count = this.querySelector('[data-sgc-count]');
        count.textContent = 'Search unavailable. Try again.';
      } finally { if (this.isConnected && this.q === q) this.setBusy(false); }
    }
    drawSearch() {
      const count = this.matches.length;
      this.querySelector('[data-sgc-grid]').replaceChildren(...this.matches.slice(0, this.searchLimit).map(card => card.cloneNode(true)));
      this.querySelector('[data-sgc-empty]').hidden = count > 0;
      this.querySelector('[data-sgc-count]').textContent = `${count} ${count === 1 ? 'product' : 'products'}`;
      const pagination = this.querySelector('[data-sgc-pagination]');
      pagination.replaceChildren();
      const shown = document.createElement('p'); shown.textContent = `Showing ${Math.min(this.searchLimit, count)} of ${count} products`; pagination.append(shown);
      if (count > this.searchLimit) {
        const more = document.createElement('a'); more.href = location.href; more.dataset.sgcSearchMore = ''; more.textContent = 'Show more products ↓'; pagination.append(more);
      }
      const chips = this.querySelector('[data-sgc-chips]');
      chips.querySelector('[data-sgc-search-chip]')?.remove();
      const chip = document.createElement('a'); chip.href = location.href; chip.dataset.sgcClearSearch = ''; chip.dataset.sgcSearchChip = ''; chip.textContent = `“${this.q}” ×`; chip.setAttribute('aria-label', `Remove ${this.q} search`); chips.prepend(chip); chips.hidden = false;
    }
  }
  customElements.define('sg-collection-catalogue', CollectionCatalogue);
})();
